import { Badge, ViewPlaceholder } from '@vrplatform/components';
import { useApiQuery, useTeam } from '@vrplatform/data';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  CalendarEventIcon,
  CircleDollarIcon,
  ClipboardTextIcon,
  FileCheckIcon,
  HashtagIcon,
  HomeIcon,
  LoaderIcon,
  Lock2Icon,
  UserIcon,
} from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { Drawer } from '@vrplatform/components-legacy';
import {
  type Maybe,
  day,
  ensure,
  formatCurrency,
  hasValue,
  sum,
} from '@vrplatform/utils';
import {
  Anchor,
  Box,
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Title,
  rem,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { type ComponentProps, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useListingDetailDrawer } from '../../views/listings/drawer';
import {
  DrawerCollapsableTable,
  DrawerHeader,
  DrawerInfoCard,
  DrawerInfoCardLockBadges,
  DrawerInfoCardOwnerStatementsLinks,
} from '../_components';
import { getDrawerInfoCardLockReasonBadges } from '../_components/DrawerInfoCardLockBadges.utils';
import { InfoCardReconciliationItem } from '../deposit-drawer/DepositInfoCard';
import { DepositExpenseTransferIssues } from '../expense-drawer/DepositExpenseTransferIssues';
import { useOwnerDetailDrawer } from '../owner-drawers';
import { BankTransferEllipsisMenuItems } from './BankTransferEllipsisMenuItems';
import { BankTransferEllipsisMenuModals } from './BankTransferEllipsisMenuModals';
import { useBankTransferDetailDrawer } from './useBankTransferDetailDrawer';

export const STORYBOOK_BANK_TRANSFER_DETAIL_QUERY_KEY = 'bank-transfer-detail';

function useBankTransferQuery(id: Maybe<string>) {
  const [{ id: teamId }] = useTeam();

  const query = useApiQuery(
    async ($api, signal, { id }) => {
      if (!id) return null;

      const bankTransfer = await $api
        .GET('/transactions/{id}', {
          params: {
            path: {
              id,
            },
          },
          signal,
        })
        .then((result) => {
          if (result.error) throw result.error;

          const data = result.data;

          return {
            ...data,
            amount: Math.abs(data.amount),
            lines: data.lines.map((line) => ({
              ...line,
              amount: Math.abs(line.amount),
            })),
          };
        });

      const lines = bankTransfer.lines ?? [];

      const attachedToOwnerStatementIds = lines
        .map((line) => line.ownerStatementId)
        .filter(hasValue);

      if (bankTransfer.payment.bankRecordIds.length) {
        const bankRecord = await $api.GET('/bank-records/{id}', {
          params: {
            path: {
              id: bankTransfer.payment.bankRecordIds[0],
            },
          },
          signal,
        });

        if (bankRecord.error) throw bankRecord.error;

        return {
          ...bankTransfer,
          bankRecord: bankRecord.data,
          isReconciled: !!bankRecord.data,
          attachedToOwnerStatementIds,
          isPublished: !!attachedToOwnerStatementIds.length,
        };
      }

      return {
        ...bankTransfer,
        bankRecord: undefined,
        isReconciled: false,
        attachedToOwnerStatementIds,
        isPublished: !!attachedToOwnerStatementIds.length,
      };
    },
    {
      queryGroup: 'bank-transfers',
      uniqueQueryKey: 'bank-transfer-detail',
      variables: {
        id,
        teamId,
      },
      skip: !id,
      queryKeys: ['accounts', 'owner-statements'],
      storybookQueryKey: STORYBOOK_BANK_TRANSFER_DETAIL_QUERY_KEY,
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}

type BankTransfer = NonNullable<
  ReturnType<typeof useBankTransferQuery>['data']
>;

export const BankTransferDetailDrawer = () => {
  const { opened, close, bankTransferId } = useBankTransferDetailDrawer();
  const { isLoading, data: bankTransfer } =
    useBankTransferQuery(bankTransferId);

  const [modal, setModal] = useState<'delete' | 'edit' | 'unreconcile' | null>(
    null
  );

  return (
    <>
      <BankTransferEllipsisMenuModals
        bankTransfer={bankTransfer}
        deleteModal={{
          opened: modal === 'delete' && !!bankTransfer,
          closeModal: () => setModal(null),
        }}
        editModal={{
          opened: modal === 'edit' && !!bankTransfer,
          closeModal: () => setModal(null),
        }}
        unreconcileModal={{
          opened: modal === 'unreconcile' && !!bankTransfer,
          closeModal: () => setModal(null),
        }}
      />
      <Drawer opened={opened} onClose={close} size={550}>
        <DrawerHeader
          closeDrawer={close}
          loading={isLoading}
          type="Bank transfer"
          title={
            bankTransfer && (
              <Group wrap="nowrap" gap="xs" mt={rem(5)}>
                <Box>
                  <Title order={4} fw={500} component="p" m={0}>
                    {bankTransfer.description || 'Bank transfer'}
                  </Title>
                </Box>
              </Group>
            )
          }
          menuItems={
            bankTransfer && (
              <BankTransferEllipsisMenuItems
                openDeleteModal={() => setModal('delete')}
                openEditModal={() => setModal('edit')}
                openUnmatchModal={() => setModal('unreconcile')}
                bankTransfer={bankTransfer}
              />
            )
          }
        />
        {!bankTransfer && !isLoading ? (
          <ViewPlaceholder
            title="No bank transfer found"
            description="Has the bank transfer been deleted?"
            icon="CommentQuestionIcon"
            backgroundColor="transparent"
          />
        ) : (
          <Content bankTransfer={bankTransfer} isLoading={isLoading} />
        )}
      </Drawer>
    </>
  );
};

const Content = ({
  bankTransfer,
  isLoading,
}: {
  bankTransfer: Maybe<BankTransfer>;
  isLoading: boolean;
}) => {
  if (!bankTransfer) return null;

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
      }}
    >
      <DepositExpenseTransferIssues
        issues={bankTransfer.issues}
        type="transfer"
      />

      <InfoCard bankTransfer={bankTransfer} />

      <Lines lines={bankTransfer.lines} currency={bankTransfer.currency} />

      <LoadingOverlay
        visible={isLoading}
        loaderProps={{
          size: 'sm',
        }}
      />
    </Stack>
  );
};

type BankTransferLine = NonNullable<BankTransfer['lines']>[number];

const InfoCard = ({ bankTransfer }: { bankTransfer: BankTransfer }) => {
  const { open: openOwner } = useOwnerDetailDrawer();
  const { open: openListing } = useListingDetailDrawer();
  const firstLine = bankTransfer.lines[0];
  const fromBankAccountId = bankTransfer.account?.id;
  const counterpartyLine =
    bankTransfer.lines.find((line) => line.account?.id !== fromBankAccountId) ??
    bankTransfer.lines.find((line) => line.account?.id) ??
    bankTransfer.lines[0];
  const fromBankAccountName = bankTransfer.account?.name;
  const fromBankAccountLink = bankTransfer.account?.id
    ? `/reconciliation/account/${bankTransfer.account.id}`
    : null;
  const toBankAccountName = counterpartyLine?.account?.name;
  const toBankAccountLink = counterpartyLine?.account?.id
    ? `/reconciliation/account/${counterpartyLine.account.id}`
    : null;
  const lockReasonBadges = useMemo(
    () =>
      getDrawerInfoCardLockReasonBadges({
        sources: [
          {
            isLocked: bankTransfer.lock?.status === 'locked',
            lock: bankTransfer.lock,
          },
        ],
        fallbackBadge: {
          label: 'Locked',
          tooltip: 'This bank transfer is locked and cannot be edited.',
        },
      }),
    [bankTransfer.lock]
  );

  const rows = useMemo(
    () =>
      ensure<
        (ComponentProps<typeof DrawerInfoCard>['rows'][number] | undefined)[]
      >([
        lockReasonBadges.length
          ? {
              icon: Lock2Icon,
              title: 'Locked due to',
              text: <DrawerInfoCardLockBadges badges={lockReasonBadges} />,
            }
          : undefined,
        {
          icon: LoaderIcon,
          title: 'Status',
          text:
            bankTransfer.status !== 'inactive' ? null : (
              <Badge color="orange" size="md">
                Archived
              </Badge>
            ),
        },
        {
          icon: CircleDollarIcon,
          title: 'Amount',
          text: formatCurrency(
            bankTransfer.amount / 100,
            bankTransfer.currency
          ),
        },
        {
          icon: CalendarEventIcon,
          title: 'Date',
          text: day(bankTransfer.date).format('MMM D, YYYY'),
        },
        {
          icon: ArrowRightIcon,
          title: 'From bank account',
          text: fromBankAccountName ? (
            fromBankAccountLink ? (
              <Anchor
                component={Link}
                to={fromBankAccountLink}
                onClick={(event) => event.stopPropagation()}
              >
                {fromBankAccountName}
              </Anchor>
            ) : (
              fromBankAccountName
            )
          ) : (
            <Badge color="orange" size="md">
              Missing bank account
            </Badge>
          ),
        },
        {
          icon: ArrowLeftIcon,
          title: 'To bank account',
          text: toBankAccountName ? (
            toBankAccountLink ? (
              <Anchor component={Link} to={toBankAccountLink}>
                {toBankAccountName}
              </Anchor>
            ) : (
              toBankAccountName
            )
          ) : (
            <Badge color="orange" size="md">
              Missing bank account
            </Badge>
          ),
        },
        {
          icon: UserIcon,
          title: 'Contact',
          text: bankTransfer.contact ? (
            <Anchor
              component="button"
              ta="left"
              onClick={() =>
                openOwner(bankTransfer.contact!.id, 'overview', 'push')
              }
            >
              {bankTransfer.contact.name}
            </Anchor>
          ) : null,
        },
        {
          icon: HomeIcon,
          title: 'Listing',
          text: firstLine?.listing ? (
            <Anchor
              component="button"
              ta="left"
              onClick={() =>
                openListing(firstLine.listing!.id, 'overview', 'push')
              }
            >
              {firstLine.listing.name}
            </Anchor>
          ) : null,
        },
        {
          icon: HashtagIcon,
          title: 'Reference',
          text: bankTransfer.uniqueRef,
        },
        {
          icon: FileCheckIcon,
          title: 'Reconciliation',
          text: (
            <InfoCardReconciliationItem
              bankRecordIds={bankTransfer.payment.bankRecordIds}
            />
          ),
        },
        {
          icon: ClipboardTextIcon,
          title: 'Statement Paid',
          text: bankTransfer.attachedToOwnerStatementIds.length ? (
            <DrawerInfoCardOwnerStatementsLinks
              ids={bankTransfer.attachedToOwnerStatementIds}
            />
          ) : null,
        },
      ]).filter(hasValue),
    [
      bankTransfer.amount,
      bankTransfer.attachedToOwnerStatementIds,
      bankTransfer.bankRecord?.id,
      bankTransfer.contact,
      bankTransfer.currency,
      bankTransfer.date,
      bankTransfer.status,
      bankTransfer.uniqueRef,
      firstLine?.listing,
      fromBankAccountName,
      fromBankAccountLink,
      lockReasonBadges,
      openListing,
      openOwner,
      toBankAccountLink,
      toBankAccountName,
    ]
  );

  return <DrawerInfoCard rows={rows} labelWidth={150} />;
};

const Lines = ({
  lines,
  currency,
}: {
  lines: BankTransferLine[];
  currency: string;
}) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: matches existing drawer pattern
  const total = useMemo(
    () => formatCurrency(sum(lines, (line) => line.amount) / 100, currency),
    [currency]
  );

  const columns = useMemo<MRT_ColumnDef<BankTransferLine>[]>(
    () => [
      {
        header: 'Description',
        accessorFn: (row) => row.description || '-',
      },
      {
        header: 'Split',
        accessorKey: 'centTotal',
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
          sx: (theme) => ({
            paddingRight: theme.spacing.xs,
            fontWeight: 'bold',
          }),
        },
        size: 100,
        Cell: ({ row }) => {
          return formatCurrency(row.original.amount / 100, currency);
        },
        Footer: !lines.length ? undefined : () => total,
      },
    ],
    [currency, lines.length, total]
  );

  return (
    <DrawerCollapsableTable
      title="Lines"
      rightSection={null}
      rowData={lines}
      columns={columns}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No lines found
          </Text>
        </Center>
      )}
    />
  );
};
